import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportGrants } from './api/grantsApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { grantColumns } from './components/GrantColumns'
import GrantFormDialog from './components/GrantFormDialog'
import { useGrants } from './hooks/useGrants'
import type { Grant, GrantUpsert } from './types'
import Badge from '../../components/ui/Badge'
import { InputSwitch } from 'primereact/inputswitch'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/** Mobil kart kebab menüsü — switch dışındaki Düzenle/Sil'i tek düğmede toplar (v2 madde 1). */
function GrantCardMenu({
  row,
  name,
  busy,
  onEdit,
  onDelete,
  t,
}: Readonly<{
  row: Grant
  /**
   * Kartın GÖRÜNEN adı — kebab düğmesinin erişilebilir adına girer. Ayrı prop: bu bileşen `t`
   * alıyor ama `localized`'ı almıyor, çağıran zaten başlığı aynı değerle çiziyor.
   */
  name: string
  busy: boolean
  onEdit: (row: Grant) => void
  onDelete: (row: Grant) => void
  t: (key: string) => string
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    { label: t('admin.grants.actions.edit'), icon: 'pi pi-pencil', command: () => onEdit(row) },
    {
      label: t('admin.grants.actions.delete'),
      icon: 'pi pi-trash',
      className: 'ui-card-menu-item--danger',
      command: () => onDelete(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        /* Erişilebilir ad hangi yetkinin menüsü olduğunu söyler: yinelenen kartlarda yalnız
           "işlemler" diyen düğmeler ekran okuyucuda ayırt edilemiyordu (THY AI review bulgusu,
           26.10.2026). Değer kartın görünen başlığıyla aynı. */
        aria-label={`${name}, ${t('admin.grants.actions.menu')}`}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/** Admin → Yetki (GRANTS) yönetim ekranı (015). Liste + ekle/düzenle + durum toggle + soft-delete. */
export default function GrantsAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, update, changeStatus, remove } = useGrants()
  const toast = useToast()
  // Kısıtlı Yetkili asistan (126): ekran salt okunur — düzenle/sil yok, switch pasif. Pencerede
  // gizli bir içerik yok (ad, kod, nesne tabloda okunuyor), o yüzden görüntüleme kipi gerekmez.
  const { readOnly } = useProxyMode()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Grant | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)
  // Arama — tüm yetkiler istemcide yüklü (size=200); anlık, TR-farkında (TR/EN ad + kod + nesne) client-side filtre.
  const [search, setSearch] = useState('')

  const content = list.data?.content
  const rows = useMemo(() => {
    const all = content ?? []
    const needle = search.trim().toLocaleUpperCase('tr-TR')
    if (!needle) return all
    return all.filter(
      (g) =>
        g.nameTr.toLocaleUpperCase('tr-TR').includes(needle) ||
        (g.nameEn ?? '').toLocaleUpperCase('tr-TR').includes(needle) ||
        g.code.toLocaleUpperCase('tr-TR').includes(needle) ||
        g.object.toLocaleUpperCase('tr-TR').includes(needle),
    )
  }, [content, search])
  const submitting = update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const clearErrors = () => {
    setNameError(null)
    setCodeError(null)
  }
  const openEdit = (row: Grant) => {
    setEditing(row)
    clearErrors()
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_CODE') {
      setCodeError(t('admin.grants.errors.duplicateCode'))
      return
    }
    if (isOptimisticLock(err)) {
      // Merkez uyarı toast'ını gösterir; ekran yalnız diyaloğu kapatır ve listeyi tazeler.
      setFormOpen(false)
      list.refetch()
    }
    // Diğer hatalar (generic/ağ vb.) merkezî onError tarafından toast'lanır.
  }

  /**
   * Yalnız <b>güncelleme</b>. Yeni yetki oluşturma arayüzden kaldırıldı (kullanıcı isteği
   * 28.09.2026): yetki kataloğu koda bağlıdır — kod sabiti {@code Grants} sınıfında tanımlı olmayan
   * bir yetki eklemek hiçbir kapıyı açmaz, yalnız listeyi kirletir. Dialog artık yalnız satırdaki
   * Düzenle'den açıldığı için {@code editing} her zaman doludur.
   *
   * <p>Sunucudaki oluşturma ucu <b>duruyor</b>; kaldırılan yalnız istemci yüzeyidir.
   */
  const onSubmit = (payload: GrantUpsert) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { ...payload, modDate: editing.modDate } },
      {
        onSuccess: () => {
          notify('success', t('admin.grants.toast.updated'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      },
    )
  }

  const onToggle = (row: Grant, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('admin.grants.toast.statusChanged')),
        onError: (err) => {
          // Domaine özel mesaj ekranda; optimistic-lock/generic merkezde (meta.handledCodes ile susar).
          if (getApiErrorCode(err) === 'GRANT_IN_USE') notify('error', t('admin.grants.errors.inUse'))
        },
      },
    )
  }

  const onDelete = (row: Grant) => {
    confirmDialog({
      header: t('admin.grants.delete.title'),
      message: t('admin.grants.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.grants.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('admin.grants.toast.deleted')),
          onError: (err) => {
            if (getApiErrorCode(err) === 'GRANT_IN_USE') notify('error', t('admin.grants.errors.inUse'))
          },
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <GrantFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        codeError={codeError}
        onClearErrors={clearErrors}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.admin.grants')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={() => exportGrants(search)} disabled={rows.length === 0} />
          </>
        }
      />

      {/* Arama (TR-farkında, TR/EN ad + kod + nesne) — sağa hizalı (diğer admin ekranlarıyla tutarlı);
          tüm yetkiler istemcide yüklü olduğundan anlık filtreler. */}
      <FilterBar
        className="ui-filterbar--wide-search"
        searchValue={search}
        searchPlaceholder={t('admin.grants.search')}
        onSearch={setSearch}
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && (
            <EmptyState title={t('admin.grants.empty.title')} description={t('admin.grants.empty.desc')} />
          )}
          {!list.isLoading &&
            rows.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active ? t('admin.grants.status.active') : t('admin.grants.status.passive')}
                    </Badge>
                    <div className="ui-card-actions ui-card-actions--compact">
                      <InputSwitch
                        className="admin-switch"
                        checked={active}
                        disabled={readOnly || busyId === row.id}
                        onChange={(e) => onToggle(row, Boolean(e.value))}
                        aria-label={row.nameTr}
                        tooltip={t('admin.grants.actions.toggle')}
                        tooltipOptions={{ position: 'bottom' }}
                      />
                      {/* Menüde yalnız yazma eylemleri var → salt okunur kipte hiç çizilmez. */}
                      {!readOnly && (
                        <GrantCardMenu
                          row={row}
                          name={localized(row.nameTr, row.nameEn)}
                          busy={busyId === row.id}
                          onEdit={openEdit}
                          onDelete={onDelete}
                          t={t}
                        />
                      )}
                    </div>
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                  </div>
                  <div className="ui-card-row">
                    <span>{row.code}</span>
                    <span>{row.object}</span>
                  </div>
                  {/* "Verildiği Roller" (118) — bilgi dar ekranda da kaybolmamalı. Yalnız SUNUM
                      eklenir: satır işlemleri ve veri değişmez, yeni interaktif öğe yoktur. */}
                  {row.roles.length > 0 && (
                    <div className="admin-role-chips">
                      {row.roles.map((r) => (
                        <Badge key={r.id} tone="neutral">
                          {localized(r.nameTr, r.nameEn)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        sortField="nameTr"
        sortOrder={1}
        emptyMessage={
          <EmptyState title={t('admin.grants.empty.title')} description={t('admin.grants.empty.desc')} />
        }
      >
        {grantColumns({ t, localized, busyId, onEdit: openEdit, onToggle, onDelete, readOnly })}
      </DataTable>
      )}
    </div>
  )
}
